import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2, GraduationCap, ArrowRight, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function resolveImg(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

// Login-style themed illustration (used as the "Default" hero background)
function DefaultHeroArt() {
  return (
    <>
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(0,122,255,0.22), transparent 55%), radial-gradient(circle at 80% 80%, rgba(255,159,10,0.14), transparent 50%), linear-gradient(135deg, #0A0F1F 0%, #07080C 60%, #0A0F1F 100%)",
        }}
      />
      <svg
        viewBox="0 0 800 800"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full opacity-[0.55]"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient id="aBook" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#007AFF" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#FF9F0A" stopOpacity="0.7" />
          </linearGradient>
          <linearGradient id="aLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(255,255,255,0.0)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.35)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.0)" />
          </linearGradient>
          <pattern id="aDot" x="0" y="0" width="34" height="34" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.05)" />
          </pattern>
        </defs>
        <rect width="800" height="800" fill="url(#aDot)" />
        <g stroke="url(#aLine)" strokeWidth="1.2">
          <line x1="60" y1="700" x2="720" y2="120" />
          <line x1="20" y1="500" x2="780" y2="320" />
        </g>
        <g transform="translate(180, 190)">
          <polygon points="220,30 420,90 220,150 20,90" fill="url(#aBook)" opacity="0.85" />
          <rect x="80" y="100" width="280" height="80" rx="4" fill="#FFFFFF" opacity="0.06" />
          <line x1="420" y1="90" x2="430" y2="220" stroke="#FF9F0A" strokeWidth="3" />
          <circle cx="430" cy="225" r="8" fill="#FF9F0A" />
        </g>
        <g transform="translate(140, 470)">
          <path d="M0 60 Q140 0 280 60 L280 240 Q140 180 0 240 Z" fill="url(#aBook)" opacity="0.7" />
          <path d="M280 60 Q420 0 560 60 L560 240 Q420 180 280 240 Z" fill="url(#aBook)" opacity="0.55" />
          <line x1="280" y1="60" x2="280" y2="240" stroke="#FFFFFF" strokeOpacity="0.4" strokeWidth="2" />
          {[80, 110, 140, 170].map((y, i) => (
            <g key={i}>
              <line x1="40" y1={y} x2="240" y2={y - 20} stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="2" />
              <line x1="320" y1={y - 20} x2="520" y2={y} stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="2" />
            </g>
          ))}
        </g>
        <g fill="#FFFFFF">
          <circle cx="640" cy="180" r="2.5" opacity="0.7" />
          <circle cx="700" cy="260" r="1.5" opacity="0.5" />
          <circle cx="80" cy="320" r="2" opacity="0.5" />
          <circle cx="560" cy="640" r="2" opacity="0.6" />
          <circle cx="700" cy="600" r="2.5" opacity="0.7" />
        </g>
      </svg>
    </>
  );
}

export default function ApplyPage() {
  const [stage, setStage] = useState(null);
  const [settings, setSettings] = useState({});
  const [data, setData] = useState({});
  const [hp, setHp] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const isEmbed = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embed") === "1";

  useEffect(() => {
    document.documentElement.classList.remove("light");
    document.documentElement.classList.add("dark");
  }, []);

  // Report content height to the parent window when embedded via iframe
  useEffect(() => {
    if (!isEmbed) return;
    // Neutralize global min-height:100vh so the reported height fits content
    const root = document.getElementById("root");
    [document.documentElement, document.body, root].forEach((el) => {
      if (el) {
        el.style.minHeight = "0";
        el.style.height = "auto";
        el.style.background = "transparent";
      }
    });
    const send = () => {
      const h = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
      );
      window.parent.postMessage({ type: "utm-apply-height", height: h }, "*");
    };
    send();
    const ro = new ResizeObserver(send);
    ro.observe(document.body);
    window.addEventListener("load", send);
    const t = setInterval(send, 1000);
    return () => {
      ro.disconnect();
      window.removeEventListener("load", send);
      clearInterval(t);
    };
  }, [isEmbed, stage, done, err]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: cfg }] = await Promise.all([
          api.get("/public/stage"),
          api.get("/app-settings").catch(() => ({ data: {} })),
        ]);
        setStage(s);
        setSettings(cfg || {});
      } catch (e) {
        setErr("Form pendaftaran belum tersedia.");
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await api.post("/public/leads", { data, hp });
      setDone(true);
    } catch (ex) {
      if (ex.response?.status === 429) {
        setErr("Terlalu banyak pengiriman. Silakan coba lagi beberapa menit.");
      } else {
        setErr(ex.response?.data?.detail || "Gagal mengirim form");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const heroImg = resolveImg(settings.apply_hero_image_url);
  const heroTitle = settings.apply_hero_title || "Ayo bergabung bersama UT Mataram";
  const heroSubtitle =
    settings.apply_hero_subtitle || "Perguruan Tinggi Negeri Pelopor Kuliah Online di NTB";
  const brandName = settings.app_name || "UT Mataram";

  if (done) {
    return (
      <div className={isEmbed ? "bg-[#0C0C0E] p-3 flex justify-center" : "min-h-screen flex items-center justify-center p-6 bg-[#070707] bg-grid-pattern"}>
        <div className="bg-white/[0.04] border border-white/10 backdrop-blur-xl p-10 max-w-lg text-center rounded-2xl" data-testid="apply-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#32D74B]/15 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-[#32D74B]" strokeWidth={1.5} />
          </div>
          <h2 className="font-heading text-2xl text-white font-semibold mt-5">Pendaftaran Terkirim</h2>
          <p className="text-sm text-white/60 mt-3 leading-relaxed">
            Terima kasih telah mendaftar di {brandName}. Tim admisi kami akan segera menghubungi Anda lewat WhatsApp.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={isEmbed ? "bg-[#0C0C0E] p-3 flex justify-center" : "min-h-screen bg-[#070707] bg-grid-pattern flex items-center justify-center p-4 sm:p-6"}>
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#0C0C0E]">
        {/* Hero panel */}
        <div className="relative min-h-[240px] lg:min-h-full overflow-hidden">
          {heroImg ? (
            <img
              src={heroImg}
              alt="Mahasiswa UT Mataram"
              data-testid="apply-hero-image"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <DefaultHeroArt />
          )}
          {/* Readability overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />

          <div className="relative z-10 h-full flex flex-col justify-center p-6 sm:p-8 lg:p-10">
            <div className="max-w-sm space-y-4">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-[10px] tracking-[0.22em] uppercase text-white/90 backdrop-blur-sm">
                <Sparkles className="w-3 h-3 text-[#FF9F0A]" strokeWidth={2} />
                Pendaftaran Mahasiswa Baru
              </div>

              {/* Title */}
              <h2
                data-testid="apply-hero-title"
                className="font-heading text-3xl sm:text-4xl font-bold text-white leading-[1.1] tracking-tight drop-shadow-lg"
              >
                {heroTitle}
              </h2>

              {/* Subtitle */}
              <p data-testid="apply-hero-subtitle" className="text-sm sm:text-base text-white/80 leading-relaxed drop-shadow max-w-xs">
                {heroSubtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Form panel */}
        <div className="bg-[#0C0C0E] p-6 sm:p-8" data-testid="apply-form">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">Formulir</div>
          <h1 className="font-heading text-2xl sm:text-3xl text-white font-bold tracking-tight mt-1">
            Daftar Calon Mahasiswa
          </h1>
          <p className="text-sm text-white/50 mt-2 mb-6">
            Lengkapi data berikut. Tim admisi akan menghubungi Anda dalam 1×24 jam.
          </p>

          {!stage ? (
            <div className="text-sm text-white/60">
              {err ? err : (<><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Memuat form...</>)}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {/* Honeypot — hidden from real users, bots tend to fill it */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                data-testid="apply-honeypot"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              {(stage.fields || []).map((f) => (
                <div key={f.key}>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">
                    {f.label} {(f.key === "nama" || f.key === "no_wa") && <span className="text-[#FF453A]">*</span>}
                  </label>
                  {f.type === "select" ? (
                    <Select value={data[f.key] || ""} onValueChange={(v) => setData({ ...data, [f.key]: v })}>
                      <SelectTrigger data-testid={`apply-field-${f.key}`} className="bg-black/30 border-white/10 text-white">
                        <SelectValue placeholder="Pilih..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : f.type === "textarea" ? (
                    <textarea
                      data-testid={`apply-field-${f.key}`}
                      value={data[f.key] || ""}
                      onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                      rows={3}
                      className="w-full bg-black/30 border border-white/10 rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  ) : (
                    <Input
                      data-testid={`apply-field-${f.key}`}
                      type={f.type === "date" ? "date" : f.type === "number" ? "number" : f.type === "phone" ? "tel" : "text"}
                      value={data[f.key] || ""}
                      onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                      placeholder={f.key === "no_wa" ? "62812xxxx" : ""}
                      className="bg-black/30 border-white/10 text-white placeholder:text-white/30"
                      required={f.key === "nama" || f.key === "no_wa"}
                    />
                  )}
                </div>
              ))}

              {err && (
                <div data-testid="apply-error" className="text-sm text-[#FF453A] bg-[#FF453A]/10 border border-[#FF453A]/30 rounded-md p-3">
                  {err}
                </div>
              )}

              <Button
                data-testid="apply-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-gradient-to-r from-primary to-[#0056B3] hover:from-[#0056B3] hover:to-primary text-white font-semibold group"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Kirim Pendaftaran
                    <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-1.5 text-white/30 text-[11px] font-mono tracking-wider pt-2">
                <GraduationCap className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span>© UT Mataram 2026</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
